// @ts-nocheck
/**
 * TeamLeader Workspace — My Groups Page
 * Primary working page for managing led groups, student counts, assigned tracks & courses.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { fetchTLGroupsWithStudents, type TLGroupWithStudents } from "@/lib/api/tl-dashboard";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, AlertTriangle, ChevronRight, FolderOpen } from "lucide-react";

export const Route = createFileRoute("/_authenticated/team-leader/groups")({
  component: MyGroupsPage,
});

// Completion = students who PASSED, over the whole roster. Shared by the card bar and the
// "least progress" sort so the two can never disagree.
function passedPercent(g: TLGroupWithStudents): number {
  return g.studentCount > 0 ? (g.completedCount / g.studentCount) * 100 : 0;
}

const SORT_OPTIONS = [
  { value: "atrisk", label: "At risk first" },
  { value: "progress", label: "Least progress" },
  { value: "students", label: "Most students" },
  { value: "name", label: "Name (A–Z)" },
];

function MyGroupsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  // Default is triage, not API order: the groups that need a leader's attention come first.
  const [sortBy, setSortBy] = useState("atrisk");
  const [atRiskOnly, setAtRiskOnly] = useState(false);

  // FOLLOW-UP DEBT: no paginated backend endpoint exists for this list — GET
  // /api/teamleader/groups (TeamLeaderController.GetMyGroups -> GetMyGroupsAsync)
  // returns the full List<GroupDto> with no pageNumber/pageSize support, so this
  // stays client-side filter/search over the full (naturally small, per-leader) set
  // instead of usePaginationState + PaginationControls like team-leader/students.tsx.
  const { data: groups = [], isLoading } = useQuery({
    queryKey: ["tl", "groups-with-students"],
    queryFn: fetchTLGroupsWithStudents,
    enabled: !!user?.id,
    staleTime: 30_000,
  });

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groups;
    const q = search.toLowerCase();
    return groups.filter(
      (g) =>
        g.name.toLowerCase().includes(q) ||
        (g.description && g.description.toLowerCase().includes(q)) ||
        (g.tracks && g.tracks.some((t) => t.name.toLowerCase().includes(q))) ||
        (g.courses && g.courses.some((c) => c.name.toLowerCase().includes(q))),
    );
  }, [groups, search]);

  // Search narrows, the at-risk chip narrows further, then the chosen order is applied. Sorting a
  // copy keeps the query cache's array untouched.
  const visibleGroups = useMemo(() => {
    const list = atRiskOnly
      ? filteredGroups.filter((g) => (g.atRiskCount || 0) > 0)
      : filteredGroups;
    const sorted = [...list];
    if (sortBy === "atrisk") {
      sorted.sort(
        (a, b) => (b.atRiskCount || 0) - (a.atRiskCount || 0) || a.name.localeCompare(b.name),
      );
    } else if (sortBy === "progress") {
      sorted.sort((a, b) => passedPercent(a) - passedPercent(b));
    } else if (sortBy === "students") {
      sorted.sort((a, b) => (b.studentCount || 0) - (a.studentCount || 0));
    } else if (sortBy === "name") {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    }
    return sorted;
  }, [filteredGroups, atRiskOnly, sortBy]);

  const totalStudents = useMemo(
    () => groups.reduce((acc, g) => acc + (g.studentCount || 0), 0),
    [groups],
  );

  const totalAtRisk = useMemo(
    () => groups.reduce((acc, g) => acc + (g.atRiskCount || 0), 0),
    [groups],
  );

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">My Groups</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Primary workspace for monitoring and managing your assigned groups
          </p>
        </div>
        <Badge variant="outline" className="shrink-0 px-3 py-1 text-sm font-medium">
          {groups.length} groups · {totalStudents} students
        </Badge>
      </div>

      {/* Search · sort · triage */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search groups by name, track, or course..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          <span className="text-xs font-medium text-muted-foreground">Sort</span>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[190px] text-[13px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} className="text-[13px]">
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Offered only when there is something to triage — an always-visible chip reading
              "At risk only · 0" invites a click that empties the page. */}
          {totalAtRisk > 0 && (
            <button
              type="button"
              onClick={() => setAtRiskOnly((v) => !v)}
              aria-pressed={atRiskOnly}
              className={[
                "flex h-9 cursor-pointer items-center gap-1.5 rounded-md border px-3 text-[13px] font-semibold transition-colors",
                atRiskOnly
                  ? "border-destructive/40 bg-destructive text-destructive-foreground"
                  : "border-destructive/25 bg-destructive-muted text-destructive hover:bg-destructive/15",
              ].join(" ")}
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              At risk only · {totalAtRisk}
            </button>
          )}
        </div>
      </div>

      {/* Groups Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="overflow-hidden">
              <CardContent className="p-4 space-y-3">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-12 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : visibleGroups.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            <FolderOpen className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="font-semibold text-lg">
              {atRiskOnly
                ? "No groups have students at risk"
                : search
                  ? "No matching groups found"
                  : "No groups assigned to you yet"}
            </p>
            <p className="text-sm mt-1">
              {atRiskOnly
                ? "Clear the at-risk filter to see all your groups."
                : search
                  ? "Try adjusting your search terms"
                  : "Groups assigned to your account will appear here."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {visibleGroups.map((group) => {
            // Use the counts fetchTLGroupsWithStudents already resolves server-side, one query
            // each, over the WHOLE group.
            //
            // These were previously recomputed here by filtering `group.students` (only the first
            // 100 rows) for status values "approved"/"active"/"at_risk" — but those rows carry a
            // TrainingStatus ("InTraining"/"Passed"), so no row ever matched. Both counts were
            // therefore always 0, which is why a 153-student group showed "0 Active", 0% and never
            // an at-risk badge.
            const { studentCount, activeCount, completedCount, atRiskCount } = group;

            const completionPercent = Math.round(passedPercent(group));
            const trackNames = group.tracks?.map((t) => t.name) ?? [];
            const courseCount = group.courses?.length ?? 0;
            const openGroup = () =>
              navigate({ to: "/team-leader/groups/$groupId", params: { groupId: group.id } });

            return (
              <Card
                key={group.id}
                // The card already had hover-lift and a colour-changing title — a strong "I'm
                // clickable" signal with no click handler behind it. Now it opens the group, and
                // does so from the keyboard too.
                role="button"
                tabIndex={0}
                aria-label={`Open group ${group.name}`}
                onClick={openGroup}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openGroup();
                  }
                }}
                className="group flex flex-col gap-3 border p-4 transition-all duration-200 hover:shadow-xl hover:-translate-y-1 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {/* Identity. The old bordered footer carried Batch and an "Open Group" button;
                    the batch moved up here and the whole card is the button, so the chevron is
                    the only affordance the footer was adding. */}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <h2 className="truncate text-base font-bold leading-5 text-foreground group-hover:text-primary transition-colors">
                        {group.name}
                      </h2>
                      {atRiskCount > 0 && (
                        <Badge variant="destructive" className="shrink-0 gap-1 text-[10px]">
                          <AlertTriangle className="h-3 w-3" />
                          {atRiskCount} at risk
                        </Badge>
                      )}
                    </div>
                    <p className="truncate text-[11.5px] leading-[15px] text-muted-foreground mt-1">
                      Batch {group.batchName || "Standard"} ·{" "}
                      {courseCount === 0
                        ? "no courses yet"
                        : `${courseCount} course${courseCount === 1 ? "" : "s"}`}
                    </p>
                  </div>
                  <ChevronRight className="h-[18px] w-[18px] shrink-0 text-muted-foreground opacity-60 transition-transform group-hover:translate-x-0.5" />
                </div>

                {/* Progress */}
                <div>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Passed training
                    </span>
                    <span className="text-[13px] font-semibold text-foreground">
                      {completionPercent}%
                      <span className="ml-1 text-[11px] font-normal text-muted-foreground">
                        ({completedCount}/{studentCount})
                      </span>
                    </span>
                  </div>
                  <Progress value={completionPercent} className="mt-1.5 h-2" />
                </div>

                {/* Metrics — labelled for what the numbers actually are. "Active" was ambiguous
                    (online? enrolled?); the count is students still In Training. The icons that
                    used to sit beside each number are gone: at this size they cost a third of the
                    cell width and carried no information the label didn't. */}
                <div className="grid grid-cols-3 rounded-lg bg-accent/40 p-2.5">
                  <div className="min-w-0">
                    <p className="text-[15px] font-bold leading-[18px] text-foreground">
                      {studentCount}
                    </p>
                    <p className="truncate text-[10px] font-medium leading-[13px] text-muted-foreground">
                      Students
                    </p>
                  </div>
                  <div className="min-w-0 border-l pl-2.5">
                    <p className="text-[15px] font-bold leading-[18px] text-foreground">
                      {activeCount}
                    </p>
                    <p className="truncate text-[10px] font-medium leading-[13px] text-muted-foreground">
                      In training
                    </p>
                  </div>
                  <div className="min-w-0 border-l pl-2.5">
                    <p className="text-[15px] font-bold leading-[18px] text-success">
                      {completedCount}
                    </p>
                    <p className="truncate text-[10px] font-medium leading-[13px] text-muted-foreground">
                      Passed
                    </p>
                  </div>
                </div>

                {/* Tracks. The course list used to be a comma-joined line that truncated
                    mid-name; the count now lives in the meta line above instead. */}
                <div className="flex flex-wrap items-center gap-1.5">
                  {trackNames.length > 0 ? (
                    trackNames.map((name) => (
                      <Badge key={name} variant="secondary" className="text-[10px]">
                        {name}
                      </Badge>
                    ))
                  ) : (
                    <span className="inline-flex h-5 items-center rounded-md border border-dashed px-2 text-[10px] font-medium text-muted-foreground">
                      No track assigned
                    </span>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
